import { useMemo } from 'react';
import classNames from 'classnames';
import { Tooltip } from 'antd';

import styles from './styles.module.scss';

import type { IKeyNoteData } from 'infrastructure/interfaces';

const collapseWhitespace = (value: string) => value.replace(/\s+/g, ' ').trim();

const stripHtml = (value: string) => {
  if (!value) return '';

  let output = '';
  let insideTag = false;

  for (let i = 0; i < value.length; i += 1) {
    const char = value[i];

    if (char === '<') {
      insideTag = true;
    } else if (char === '>') {
      insideTag = false;
    } else if (!insideTag) {
      output += char;
    }
  }

  return collapseWhitespace(output);
};

const getLatestNote = (notes: IKeyNoteData[]): IKeyNoteData | null => {
  if (!notes?.length) return null;

  return notes.reduce<IKeyNoteData | null>((latest, current) => {
    if (!current) return latest;
    if (!latest) return current;

    const currentTime = new Date(current.createdAt ?? 0).getTime();
    const latestTime = new Date(latest.createdAt ?? 0).getTime();

    return currentTime > latestTime ? current : latest;
  }, null);
};

interface INotePreviewProps {
  notes?: IKeyNoteData[];
  className?: string;
}

const NotePreview = ({ notes = [], className }: INotePreviewProps) => {
  const latestNote = useMemo(() => getLatestNote(notes), [notes]);
  const plainText = latestNote?.note ? stripHtml(latestNote.note) : '';

  if (!plainText) {
    return null;
  }

  const content = (
    <span className={classNames(styles.preview, className)}>{plainText}</span>
  );

  return (
    <Tooltip
      mouseEnterDelay={0.3}
      overlayClassName={styles['note-preview-tooltip']}
      title={<span className={styles['tooltip-content']}>{plainText}</span>}
    >
      {content}
    </Tooltip>
  );
};

export default NotePreview;
